import React, { useState } from "react";
import { shareApi } from "../../lib/api";
import { Card } from "../shared";
import { toast } from "sonner";
import { ThumbsDown, HandCoins, CheckCircle2 } from "lucide-react";

export const PASS_REASONS = [
  "Returns too low for the risk", "Hold period too long", "Check size / minimum doesn't fit", "Leverage or DSCR too tight",
  "Industry or geography outside mandate", "Key-person / management risk", "Valuation too high", "Timing — no capacity right now", "Prefer a different structure or class",
];

export function DecideSwitch({ tab, onChange, hasInvest, hasPass }) {
  if (!hasInvest || !hasPass) return null;
  return (
    <div className="ir-decide-switch" data-testid="ir-decide-switch">
      <button className={tab === "invest" ? "on" : ""} onClick={() => onChange("invest")} data-testid="ir-decide-invest"><HandCoins size={13} /> Commit equity</button>
      <button className={tab === "pass" ? "on pass" : ""} onClick={() => onChange("pass")} data-testid="ir-decide-pass"><ThumbsDown size={13} /> Pass on this deal</button>
    </div>
  );
}

export function PassTab({ shareId, sessionId, investor, title, onInvest }) {
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const [revisit, setRevisit] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const toggle = (r) => setReasons((xs) => (xs.includes(r) ? xs.filter((x) => x !== r) : [...xs, r]));
  const submit = async () => {
    if (!reasons.length && !note.trim()) return toast.error("Pick at least one reason or leave a note");
    setBusy(true);
    try {
      const r = await shareApi.pass(shareId, { sessionId, name: investor.name, email: investor.email, reasons, note, revisit });
      setDone(r.pass);
      toast.success("Thanks — your feedback was sent to the deal team");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record your pass"); }
    finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="ir-screen" data-testid="ir-pass-done">
        <Card title="You passed on this deal">
          <div className="ir-pass-done"><CheckCircle2 size={22} /><div><b>Recorded — thank you, {investor.name || "investor"}.</b><span>{done.revisit ? "We'll only come back to you if the terms change materially." : "We won't re-approach you on this offering."}</span></div></div>
          <div className="ir-pass-summary">{(done.reasons || []).map((r) => <span className="tag" key={r}>{r}</span>)}{done.note && <p className="ir-para sm">“{done.note}”</p>}</div>
          {onInvest && <button className="btn btn-sm" onClick={onInvest} data-testid="ir-pass-undo">Changed your mind? Review the ask</button>}
        </Card>
      </div>
    );
  }
  return (
    <div className="ir-screen" data-testid="ir-pass">
      <div className="ir-deck-head" style={{ marginTop: 0 }}>
        <span className="ir-eyebrow">Not for you?</span>
        <h2 className="ir-deck-h" data-testid="ir-pass-headline">Pass on {title} — tell us why so we bring you better-fitting deals</h2>
      </div>
      <Card title="Reasons for passing" testid="ir-pass-card">
        <div className="ir-pass-grid" data-testid="ir-pass-reasons">
          {PASS_REASONS.map((r) => (
            <button key={r} className={`ir-reason ${reasons.includes(r) ? "on" : ""}`} onClick={() => toggle(r)} data-testid={`ir-pass-reason-${PASS_REASONS.indexOf(r)}`}>{r}</button>
          ))}
        </div>
        <div className="field" style={{ marginTop: 12 }}><label>Anything else (optional)</label>
          <textarea className="inp" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What would have made this a yes?" data-testid="ir-pass-note" /></div>
        <label className="chk" style={{ marginTop: 6 }}><input type="checkbox" checked={revisit} onChange={(e) => setRevisit(e.target.checked)} data-testid="ir-pass-revisit" /> Keep me in mind if the terms change</label>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="ir-pass-submit"><ThumbsDown size={13} style={{ marginRight: 5 }} />{busy ? "Sending…" : "Pass on this deal"}</button>
          {onInvest && <button className="btn" onClick={onInvest} data-testid="ir-pass-back">Back to the ask</button>}
        </div>
        <div className="ir-foot-note">Your pass is shared with the sponsor only. It does not affect any other offering.</div>
      </Card>
    </div>
  );
}
